import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  Query,
  ReportAncestryAncient,
  ReportAncestryAncientChartQueriesFindOneArgs,
  ReportAncestryLineage,
  ReportAncestryModern,
  ReportAncestrySettingQueryResult
} from '@interfaces/schema';

/**
 * Service for fetching report ancestry data.
 */
@Injectable()
export class ReportAncestryService {
  private readonly apollo = inject(Apollo);

  /**
   * @description Fetch ancestry modern data.
   * @returns {Observable<ReportAncestryModern | undefined>}
   */
  fetchAncestryModern(): Observable<ReportAncestryModern | undefined> {
    const query = gql`
      query findOneModernAncestry(
        $continentOptions: ReportAncestryModernContinentsQueryOptions
      ) {
        Report {
          Ancestry {
            Modern {
              findOne {
                id
                user_id
                continents(options: $continentOptions) {
                  id
                  average
                  continents_code
                  continents
                  continents_prop
                  subcontinents {
                    id
                    sub_continents
                    sub_continents_code
                    sub_continents_prop
                    countries {
                      id
                      countries
                      countries_code
                      countries_prop
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query })
      .pipe(map(({ data }) => data?.Report?.Ancestry?.Modern?.findOne));
  }

  /**
   * Fetch ancient data.
   *
   * @param {ReportAncestryAncientChartQueriesFindOneArgs} variables ReportAncestryAncientChartQueriesFindOneArgs.
   * @returns {Observable<ReportAncestryAncient>}
   */
  fetchAncient(
    variables?: ReportAncestryAncientChartQueriesFindOneArgs
  ): Observable<ReportAncestryAncient | undefined> {
    const query = gql`
      query findOneAncestryAncient(
        $options: ReportAncestryAncientChartQueryOptions
      ) {
        Report {
          Ancestry {
            Ancient {
              findOne {
                id
                charts(options: $options) {
                  id
                  age
                  estimates
                  average
                  region
                  region_pop
                }
                maps {
                  age
                  region
                  estimates
                  lat
                  long
                  ancestry_ancient_id
                }
              }
            }
          }
        }
      }
    `;

    return this.apollo
      .query<Query>({
        query,
        variables
      })
      .pipe(map(({ data }) => data?.Report?.Ancestry?.Ancient?.findOne));
  }

  /**
   * Fetch ancestry lineage data.
   *
   * @returns {Observable<ReportAncestryLineage | undefined>}
   */
  fetchLineage(): Observable<ReportAncestryLineage | undefined> {
    const query = gql`
      query findOneAncestryLineage {
        Report {
          Ancestry {
            Lineage {
              findOne {
                id
                mtdna {
                  from_lat
                  from_long
                  to_lat
                  to_long
                  haplogroup
                }
                ydna_belong_to {
                  haplogroup
                  description
                }
                mtdna_belong_to {
                  haplogroup
                  description
                }
                ydna {
                  from_lat
                  from_long
                  to_lat
                  to_long
                  haplogroup
                }
              }
            }
          }
        }
      }
    `;

    return this.apollo
      .query<Query>({
        query
      })
      .pipe(map(({ data }) => data?.Report?.Ancestry?.Lineage?.findOne));
  }

  /**
   * Fetch settings.
   *
   * @returns {Observable<ReportAncestrySettingQueryResult | undefined>}
   */
  fetchSettings(): Observable<ReportAncestrySettingQueryResult | undefined> {
    const query = gql`
      query ancestrySetting {
        Report {
          Ancestry {
            Setting {
              findAll {
                count
                results {
                  id
                  resource
                  value
                  vendorBioItId
                }
              }
            }
          }
        }
      }
    `;

    return this.apollo
      .query<Query>({
        query
      })
      .pipe(map(({ data }) => data?.Report?.Ancestry?.Setting?.findAll));
  }
}
